import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import { MessagingModeRadio } from "./MessagingModeRadio";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription as AlertDialogDesc,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle as AlertDialogTitleComp,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Edit3Icon, MailIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageVariables,
  MessageVariable,
} from "@/components/dashboard_campaign/brief/MessageVariables";
import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useCampaignStore } from "@/stores";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
} from "@/components/ui/frame";
import { ChipsInput } from "@/components/ui/chips-input";
import { isEveCampaign } from "@/lib/eve";
import { OfferReviewFrame } from "./OfferReviewFrame";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUBJECT_VARIABLES: MessageVariable[] = [
  { variable: "creator_username", description: "Creator's username" },
  { variable: "organization_name", description: "Your organization's name" },
  { variable: "campaign_name", description: "Campaign name" },
  { variable: "campaign_platform", description: "Campaign platform" },
];

const OUTREACH_TEMPLATE_VARIABLES: MessageVariable[] = [
  { variable: "creator_fullname", description: "Creator's full name" },
  { variable: "creator_username", description: "Creator's username" },
  {
    variable: "creator_summary",
    description: "AI summary of creator's content",
  },
  { variable: "user_name", description: "Your name (sender)" },
  { variable: "organization_name", description: "Your organization name" },
  {
    variable: "organization_description",
    description: "Organization description",
  },
  { variable: "organization_website", description: "Organization website URL" },
  { variable: "campaign_name", description: "Campaign name" },
  {
    variable: "campaign_platform",
    description: "Platform (TikTok, Instagram, YouTube)",
  },
  {
    variable: "partnership",
    description: "Partnership details (paid, gifting, amount, etc.)",
  },
];

/**
 * Settings → Messaging (split out of "Email & Messaging" on 2026-10-22):
 * messaging mode + custom subject, the Initial Outreach Template and the
 * default CC emails, plus the Offers switch on Amy campaigns. Sending mechanics (autopilot, schedule, mailboxes,
 * limits) live in EmailSection.
 */
export function MessagingSection() {
  const { campaign: c, campaignId, updateCampaignField, updatingCampaignField } = useCampaignStore();
  const { getToken } = useAuth();

  const [customSubject, setCustomSubject] = useState(
    c?.custom_email_subject || "",
  );

  const [outreachTemplate, setOutreachTemplate] = useState(
    c?.outreach_template || "",
  );
  const [templateOpen, setTemplateOpen] = useState(false);
  const savedTemplate = c?.outreach_template?.trim() || "";
  const openTemplateEditor = () => {
    setOutreachTemplate(c?.outreach_template || "");
    setTemplateOpen(true);
  };
  const saveTemplate = (value: string) => {
    void handleUpdateCampaignField("outreach_template", value);
    setTemplateOpen(false);
  };

  const [ccEmails, setCcEmails] = useState<string[]>(c?.cc_emails ?? []);

  useEffect(() => {
    if (c) setCcEmails(c.cc_emails ?? []);
  }, [c]);

  const handleUpdateCampaignField = async (field: string, value: string) => {
    const token = await getToken();
    if (!token || !campaignId) return console.error("No token found");
    updateCampaignField(field, value, campaignId, token);
  };

  const persistCcEmails = async (next: string[]) => {
    const token = await getToken();
    if (!token || !campaignId) return console.error("No token found");
    await updateCampaignField(
      "cc_emails",
      JSON.stringify(next),
      campaignId,
      token,
    );
  };

  const handleAddCcEmail = async (email: string) => {
    const next = [...ccEmails, email];
    setCcEmails(next);
    await persistCcEmails(next);
  };

  const handleRemoveCcEmail = async (email: string) => {
    const next = ccEmails.filter((e) => e !== email);
    setCcEmails(next);
    await persistCcEmails(next);
  };

  return (
    <div className="space-y-6">
      <Frame>
        <FrameHeader>
          <FrameTitle>Messaging</FrameTitle>
        </FrameHeader>
        <MessagingModeRadio
          value={c?.messaging === "approval" ? "approval" : "automatic"}
          onValueChange={(mode) => handleUpdateCampaignField("messaging", mode)}
          disabled={updatingCampaignField}
        />
        <FramePanel>
          <div className="w-full flex md:flex-row flex-col items-center gap-2">
            <div className="flex-1 w-full">
              {c?.custom_email_subject ? (
                <Input
                  className="md:w-2/3 w-full text-sm"
                  value={c.custom_email_subject}
                  readOnly
                />
              ) : (
                <Input
                  className="md:w-2/3 w-full text-sm font-mono text-muted-foreground"
                  value="AI-generated subject"
                  readOnly
                />
              )}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="secondary"
                  onClick={() =>
                    setCustomSubject(c?.custom_email_subject || "")
                  }
                >
                  <Edit3Icon />
                  Edit Email Subject
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="sm:max-w-lg">
                <AlertDialogHeader>
                  <AlertDialogTitleComp>
                    Custom Email Subject
                  </AlertDialogTitleComp>
                  <AlertDialogDesc>
                    Custom subject for initial outreach emails. Amy's by
                    default.
                  </AlertDialogDesc>
                </AlertDialogHeader>
                <div className="space-y-4">
                  <Input
                    placeholder="Collab: {creator_username} x {organization_name}"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    autoFocus
                  />
                  <MessageVariables variables={SUBJECT_VARIABLES} />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={updatingCampaignField}
                    onClick={() =>
                      handleUpdateCampaignField("custom_email_subject", customSubject)
                    }
                  >
                    {updatingCampaignField && (
                      <Spinner />
                    )}
                    Save Subject
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </FramePanel>
      </Frame>

      {isEveCampaign(c) && <OfferReviewFrame />}

      <Frame>
        <FrameHeader>
          <FrameTitle>Initial Outreach Template</FrameTitle>
          <FrameDescription>
            {savedTemplate
              ? "Sent word for word as the first email, variables filled in."
              : "Off — Amy writes each first email herself."}
          </FrameDescription>
        </FrameHeader>
        {/* A peek at the template (or a ghosted example) fading out under the
            button — the whole panel opens the editor. */}
        <FramePanel
          role="button"
          tabIndex={0}
          aria-label={savedTemplate ? "Edit the outreach template" : "Add an outreach template"}
          onClick={openTemplateEditor}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openTemplateEditor();
            }
          }}
          className="group relative h-36 cursor-pointer overflow-hidden p-0 outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        >
          <div
            className={cn(
              // h-full: the fade spans the visible box, not the whole template
              // (a long one would fade out below the fold and just be cut off).
              "pointer-events-none h-full select-none overflow-hidden text-muted-foreground mask-[linear-gradient(to_bottom,black_15%,transparent_75%)]",
              !savedTemplate && "opacity-40",
            )}
          >
            <TemplatePreview text={savedTemplate || EXAMPLE_TEMPLATE} />
          </div>
          <div className="absolute inset-x-0 bottom-3 flex justify-center">
            {/* Looks like a button; the panel is the control. */}
            <Button asChild variant="secondary" size="sm" className="group-hover:bg-input">
              <span>
                {savedTemplate ? <Edit3Icon /> : <PlusIcon />}
                {savedTemplate ? "Edit template" : "Add template"}
              </span>
            </Button>
          </div>
        </FramePanel>
        <AlertDialog open={templateOpen} onOpenChange={setTemplateOpen}>
          <AlertDialogContent className="sm:max-w-2xl">
            <AlertDialogHeader>
              <AlertDialogTitleComp>Initial Outreach Template</AlertDialogTitleComp>
              <AlertDialogDesc>
                Amy sends it near word for word, filling in the variables.
              </AlertDialogDesc>
            </AlertDialogHeader>
            <div className="space-y-4">
              <Textarea
                className="min-h-[280px] resize-none text-sm"
                placeholder={"Hi {creator_fullname},\n\nI'm reaching out from {organization_name}..."}
                value={outreachTemplate}
                onChange={(e) => setOutreachTemplate(e.target.value)}
                autoFocus
              />
              <MessageVariables variables={OUTREACH_TEMPLATE_VARIABLES} />
            </div>
            <AlertDialogFooter>
              {savedTemplate && (
                <Button
                  variant="destructive-ghost"
                  className="sm:mr-auto"
                  disabled={updatingCampaignField}
                  onClick={() => saveTemplate("")}
                >
                  <Trash2Icon />
                  Remove template
                </Button>
              )}
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={updatingCampaignField || !outreachTemplate.trim()}
                onClick={() => saveTemplate(outreachTemplate)}
              >
                {updatingCampaignField && <Spinner />}
                Save Template
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Default CC emails</FrameTitle>
          <FrameDescription>CC'd on every activation email.</FrameDescription>
        </FrameHeader>
        <FramePanel>
          <ChipsInput
            items={ccEmails}
            onAdd={handleAddCcEmail}
            onRemove={handleRemoveCcEmail}
            addLabel="Add CC email"
            placeholder="someone@example.com"
            chipIcon={<MailIcon className="size-3" />}
            inputIcon={<MailIcon size={14} aria-hidden="true" />}
            validate={(email) =>
              EMAIL_REGEX.test(email) ? null : "Invalid email address"
            }
            duplicateMessage="Email already added"
          />
        </FramePanel>
      </Frame>
    </div>
  );
}

// Ghosted in the template panel while none is set.
const EXAMPLE_TEMPLATE =
  "Hi {creator_fullname},\n\nI'm reaching out from {organization_name} — your videos are exactly the voice we want for {campaign_name}.\n\nWould you be open to a paid collaboration?";

/** The template as the creator will read it, its {variables} marked. */
function TemplatePreview({ text }: { text: string }) {
  return (
    <p className="whitespace-pre-wrap wrap-break-word p-4 text-sm leading-relaxed">
      {text.split(/(\{\{?[a-z_]+\}?\})/g).map((part, i) =>
        /^\{\{?[a-z_]+\}?\}$/.test(part) ? (
          <span key={i} className="rounded-sm bg-primary/10 px-1 font-mono text-xs text-primary">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </p>
  );
}
